import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import {
  getInvestigationReport,
  investigatedInstant,
  type IInvestigationReport,
  type IInvestigationReportLocation,
} from '@apis/investigationReports';
import { PageLoader } from '@components/Pages/AnalyticsPage/Reports/shared/PageLoader';
import { ReportNotFoundState } from '@components/Pages/AnalyticsPage/Reports/shared/ReportNotFoundState';
import { ReportErrorState } from '@components/Pages/AnalyticsPage/Reports/shared/ReportErrorState';
import { ReportHeader } from '@components/Pages/AnalyticsPage/Reports/shared/ReportHeader/ReportHeader';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ, currentLang, formatLongDate, formatYmd } from '@utils/dateUtils';
import { useObserveLocationQuery } from './hooks/useObserveLocationQuery';
import { useSafetyEventsInRangeQuery } from './hooks/useSafetyEventsInRangeQuery';
import { useStopActivitiesQuery } from '../shared/hooks/useStopActivitiesQuery';
import { FloorStatsHeader } from './components/FloorStatsHeader';
import { VisitsCard } from './components/VisitsCard';
import { HourlyHistogram } from './components/HourlyHistogram';
import { PersonnelTimeline } from './components/PersonnelTimeline';
import { ActivitiesPanel } from './components/ActivitiesPanel';
import { SafetyEventsPanel } from './components/SafetyEventsPanel';
import { EquipmentPanel } from './components/EquipmentPanel';
import styles from './styles.module.css';

/**
 * Route entry-point for the Observe Location analytics report. Reads the
 * `siteId` + `reportId` URL params, fetches the archived
 * `IInvestigationReport` (which carries the location[] the user picked at
 * submit time), and composes seven sub-components:
 *
 *   <FloorStatsHeader>    floor + areas + four stat counts
 *   <ActivitiesPanel>     left-aside: daily activities on the floor
 *   <SafetyEventsPanel>   left-aside: safety events in the day range
 *   <EquipmentPanel>      left-aside: equipment detected at the location
 *   <VisitsCard>          top-right: first-entered / last-left / busiest
 *   <HourlyHistogram>     top-right: 24-h bar chart
 *   <PersonnelTimeline>   full-width below top row: per-person Gantt
 *
 * Three queries back the page:
 *   1. `useObserveLocationQuery`     — personnel, equipment, visits, histogram
 *   2. `useStopActivitiesQuery`      — activities on the floor for the date
 *   3. `useSafetyEventsInRangeQuery` — safety events in the day range
 *
 * The archived record is the source of truth for the title / description
 * (renameable inline via <ReportHeader>) and for the location + date the
 * queries need.
 */
export function ObserveLocationReportPage() {
  const { t } = useTranslation();
  const lang = currentLang();
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const { siteId = '', reportId = '' } = useParams<{
    siteId: string;
    reportId: string;
  }>();

  // Step 1 — fetch the archived submission. This has the location[] + date we
  // need for the data queries, plus the title/description for the renameable
  // header. `null` from the API on 404 surfaces as the not-found state.
  const recordQuery = useQuery({
    queryKey: ['investigationReport', siteId, reportId] as const,
    queryFn: ({ signal }) => getInvestigationReport(siteId, reportId, signal),
    enabled: !!siteId && !!reportId,
  });
  const record = recordQuery.data ?? null;
  const recordIsObserveLocation = record?.investigationType === 'observeLocation';

  // Derive query params from the record. When the record hasn't loaded yet (or
  // is a different type), fall back to no-op values — queries are gated by
  // `enabled` and won't fire until all required fields are non-empty.
  // No analysed instant → no day to observe. Keep the params empty so the
  // three queries stay dormant instead of fetching a substituted day, and
  // render the explicit state below.
  const analysedInstant = recordIsObserveLocation ? investigatedInstant(record) : undefined;
  const params =
    recordIsObserveLocation && analysedInstant
      ? deriveObserveLocationParams(record, analysedInstant, tz)
      : {
          areasIds: [] as string[],
          floorId: '',
          date: '',
          dayStartMs: 0,
          dayEndMs: 0,
          filterAreaId: undefined as string | undefined,
          filterFloorId: undefined as string | undefined,
        };

  // Step 2 — three data queries.
  const observe = useObserveLocationQuery({
    siteId,
    areasIds: params.areasIds,
    date: params.date,
  });
  const activities = useStopActivitiesQuery({
    siteId,
    floorId: params.floorId,
    date: params.date,
  });
  const safety = useSafetyEventsInRangeQuery({
    siteId,
    startTimestamp: params.dayStartMs,
    endTimestamp: params.dayEndMs,
    areaId: params.filterAreaId,
    floorId: params.filterFloorId,
  });

  // -- Render branches (hooks above are unconditional; only render below). --

  if (recordQuery.isLoading) return <PageLoader ariaLabelKey="reports.observeLocation.title" />;
  if (!record || !recordIsObserveLocation)
    return (
      <ReportNotFoundState
        backHref={`/analytics/${siteId}`}
        notFoundKey="reports.observeLocation.notFound"
        backKey="reports.observeLocation.notFoundBack"
      />
    );
  if (!analysedInstant)
    return (
      <ReportNotFoundState
        backHref={`/analytics/${siteId}`}
        notFoundKey="reports.shared.noAnalysedDate"
        backKey="reports.observeLocation.notFoundBack"
      />
    );

  if (observe.isError || activities.isError || safety.isError) {
    return (
      <ReportErrorState
        titleKey="reports.observeLocation.errors.bannerTitle"
        retryLabelKey="reports.observeLocation.errors.retry"
        onRetry={() => {
          observe.refetch();
          activities.refetch();
          safety.refetch();
        }}
      />
    );
  }

  // Derive section data from queries.
  const stats = {
    totalPersonnel: observe.data?.personnel.length ?? 0,
    activities: activities.data?.length ?? 0,
    safetyEvents: safety.data?.length ?? 0,
    equipment: observe.data?.equipment.length ?? 0,
  };

  const floorNick = record.location?.[0]?.floorNick ?? '';
  const areaNicks = (record.location ?? []).map((l) => l.areaNick);

  return (
    <div className={styles.page}>
      <ReportHeader
        siteId={siteId}
        reportId={reportId}
        record={record}
        defaults={{
          title: t('reports.observeLocation.title'),
          description: t('reports.observeLocation.subtitle'),
        }}
        backHref={`/analytics/${siteId}`}
        backLabel={t('reports.observeLocation.notFoundBack')}
        renameLabel={t('reports.observeLocation.rename')}
        dateText={formatLongDate(params.date, lang, tz)}
      />

      <div className={styles.content}>
        <FloorStatsHeader
          floorNick={floorNick}
          areaNicks={areaNicks}
          stats={stats}
        />

        <div className={styles.body}>
          {/* Left aside: the three panel lists */}
          <aside className={styles.aside}>
            <ActivitiesPanel
              query={{
                data: activities.data,
                isLoading: activities.isLoading,
                isError: activities.isError,
                refetch: activities.refetch,
              }}
            />
            <SafetyEventsPanel
              query={{
                data: safety.data,
                isLoading: safety.isLoading,
                isError: safety.isError,
                refetch: safety.refetch,
              }}
            />
            <EquipmentPanel
              equipment={observe.data?.equipment ?? []}
              isLoading={observe.isLoading}
            />
          </aside>

          {/* Right main: visits + histogram inside one shared card (no inner
              seam), then the personnel timeline below. */}
          <section className={styles.main}>
            <div className={styles.visitsCard}>
              <VisitsCard
                visits={observe.data?.visits ?? null}
                isLoading={observe.isLoading}
                tz={tz}
                flush
              />
              <div className={styles.visitsChart}>
                <HourlyHistogram
                  histogram={observe.data?.visits?.histogram ?? null}
                  isLoading={observe.isLoading}
                  flush
                />
              </div>
            </div>
            <PersonnelTimeline
              personnel={observe.data?.personnel ?? []}
              isLoading={observe.isLoading}
              dayStartMs={params.dayStartMs}
            />
          </section>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Internal helpers                                                            */
/* -------------------------------------------------------------------------- */

interface ObserveLocationParams {
  areasIds: string[];
  floorId: string;
  date: string;
  dayStartMs: number;
  dayEndMs: number;
  filterAreaId: string | undefined;
  filterFloorId: string | undefined;
}

/**
 * Pull the observe-location query inputs off an archived `IInvestigationReport`
 * of type `observeLocation`.
 *
 * Filter rule:
 *   - Single-area selection → filter by areaId (the specific area).
 *   - Multi-area selection → filter by floorId (all areas on the floor).
 *
 * Date: the caller resolves the analysed instant and passes it in — a record
 * without one is a render state, not a day to invent here. Formatted in the
 * site tz.
 * Day range: start-of-day → start-of-next-day (ms) in the site tz, via Luxon.
 */
function deriveObserveLocationParams(
  record: IInvestigationReport,
  analysedInstant: Date,
  tz: string,
): ObserveLocationParams {
  const location: IInvestigationReportLocation[] = record.location ?? [];
  const areasIds = location.map((l) => l.areaId);
  const floorId = location[0]?.floorId ?? '';
  const date = formatYmd(analysedInstant, tz);

  const dayStart = DateTime.fromISO(date, { zone: tz }).startOf('day');
  const dayStartMs = dayStart.toMillis();
  const dayEndMs = dayStart.plus({ days: 1 }).toMillis();

  const filterAreaId = location.length === 1 ? location[0]?.areaId : undefined;
  const filterFloorId = location.length > 1 ? floorId : undefined;

  return {
    areasIds,
    floorId,
    date,
    dayStartMs,
    dayEndMs,
    filterAreaId,
    filterFloorId,
  };
}
